import { NgFor, SlicePipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Noticia } from "@src/app/models/interfaces/noticia.interface";
import { firstValueFrom, interval, Subscription } from "rxjs";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { KebabCasePipe } from "../../../pipes/kebab-case.pipe";
import { NoticiasService } from "../../../servicios/noticias/noticias.service";

@Component({
  selector: "app-bloque-destacados",
  templateUrl: "./bloque-destacados.component.html",
  styleUrls: ["./bloque-destacados.component.scss"],
  imports: [RouterLink, KebabCasePipe, SlicePipe, NgFor],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BloqueDestacadosComponent implements OnInit, OnDestroy {
  // Referencias a elementos del DOM del componente
  @ViewChild("divNoticiasDestacadas") divNoticiasDestacadas: ElementRef;

  @Input() configPrensa: any;

  // Variables principales
  noticiasDestacadas: Noticia[] = []; // Array de noticias destacadas
  left = 0; // Posición actual de scroll
  configBloque: any; // Configuración del bloque obtenida del servicio
  selectorName: any; // Nombre del tag del componente
  numNoticias = 1; // Número de noticias a mostrar
  interSub: Subscription; // Suscripción al intervalo de scroll

  constructor(
    private readonly noticiasService: NoticiasService,
    private readonly elementRef: ElementRef,
    private readonly platformService: PlatformService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /**
   * ngOnInit
   * - Inicializa la configuración del bloque y carga las noticias destacadas.
   * - Inicia el intervalo de scroll si es un navegador.
   */
  ngOnInit(): void {
    this.selectorName = this.elementRef.nativeElement.tagName.toLowerCase();

    if (!this.configPrensa) return;
    this.configBloque = this.configPrensa.general[this.selectorName];
    this.cdr.detectChanges();

    this.numNoticias = this.configBloque.numero_noticias;

    if (this.noticiasService.getTotalUltimasNoticias()) {
      firstValueFrom(this.noticiasService.getTotalUltimasNoticias()).then((result) => {
        if (!result) return;
        this.noticiasDestacadas = result?.slice(0, this.numNoticias);
        this.cdr.detectChanges();

        if (this.platformService.isBrowser()) this.initInterval();
      });
    }
  }

  /**
   * ngOnDestroy
   * - Cancela la suscripción al intervalo para evitar fugas de memoria.
   */
  ngOnDestroy(): void {
    if (this.interSub) this.interSub.unsubscribe();
  }

  /**
   * initInterval
   * - Inicia un intervalo que ejecuta la función scrollRight cada 1500ms.
   * - Se ejecuta fuera de Angular para mejorar el rendimiento.
   */
  initInterval(): void {
    if (!this.platformService.isBrowser()) return;
    this.ngZone.runOutsideAngular(() => {
      this.interSub = interval(1500).subscribe(() => {
        this.scrollRight();
      });
    });
  }

  /**
   * scrollRight
   * - Incrementa la posición de scroll para desplazar las noticias.
   * - Resetea la posición cuando alcanza el final del scroll.
   */
  scrollRight(): void {
    this.left += 100;
    const scrollSize = this.divNoticiasDestacadas.nativeElement.scrollWidth - this.divNoticiasDestacadas.nativeElement.lastElementChild.clientWidth;
    if (this.left >= scrollSize) {
      this.left = 0;
    }
    this.divNoticiasDestacadas.nativeElement.scrollTo({ left: this.left, behavior: "smooth" });
  }

  trackByNoticia(index: number, noticia: Noticia): string {
    return noticia?.idVideo;
  }
}
